import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { QuizsetCursorSearchReqContentType } from '@/api/gen/_model/quizsetCursorSearchReqContentType';
import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { ErrorBoundaryFallback } from '@/components';
import { usePopStateDetector } from '@/hooks/usePopStateDetector';
import { PlusIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { useLibraryContentTypeQuery } from '@/module/public/hooks/useLibraryContentTypeQuery';
import { usePublicLibraryList } from '@/module/public/hooks/usePublicLibraryList';
import { useScrollRestoration } from '@/module/public/hooks/useScrollRestoration';
import { useCreateSubjectNameGetter } from '@/shared/category/useCreateSubjectNameGetter';
import { useCachedCategoryInfo } from '@/shared/category/useFetchCategoryInfo';
import { InfiniteListView } from '@/shared/components/itemList/InfiniteListView';
import { isExternalPlatformQuizAllowed, useExternalPlatform } from '@/shared/externalPlatform';
import { showCreateFolderModal } from '@/shared/features/quiz-folder';
import { LibraryListItem } from '@/shared/features/quiz-library';
import { EmptyQuizList } from '@/shared/quizList/EmptyQuizList';
import { useGetQuizListItemButtons } from '@/shared/quizList/hooks';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';
import { Button } from '@/ui_v3';
import { CreateQuizBannerItem } from './CreateQuizBannerItem';
export const QuizList = () => {
  const {
    flatData,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = usePublicLibraryList();
  const {
    searchText
  } = useSearchQuizQuery();
  const {
    contentType
  } = useLibraryContentTypeQuery();
  const isSearchMode = Boolean(searchText);
  const isPopState = usePopStateDetector();
  useScrollRestoration(!isLoading, isPopState);
  const getQuizButtons = useGetQuizListItemButtons('public');
  const {
    categoryTreeData
  } = useCachedCategoryInfo();
  const {
    mode
  } = useExternalPlatform();
  const subjectNameGetter = useCreateSubjectNameGetter(categoryTreeData?.subjectTree);

  // 외부 플랫폼 제한은 퀴즈 템플릿에만 걸린다. 폴더는 그대로 통과시킨다.
  const visibleItems = flatData.filter(item => item.kind === 'FOLDER' || isExternalPlatformQuizAllowed(item.quiz, mode));
  const buildQuizProps = (quiz: SearchQuizsetRes) => ({
    eventLocation: 'public' as const,
    isHideAuthor: false,
    alwaysDropDownMenu: false,
    subjectName: subjectNameGetter({
      subjectGroupId: quiz.categoryInfo?.subject,
      subSubjectGroupId: quiz.categoryInfo?.subSubject
    }),
    menuItems: getQuizButtons(quiz)
  });
  return <InfiniteListView items={visibleItems} isLoading={isLoading} isError={isError} hasNextPage={hasNextPage} fetchNextPage={fetchNextPage} isFetchingNextPage={isFetchingNextPage} HeaderComponent={isSearchMode ? null : <CreateQuizBannerItem />} renderItem={(item, index) => <LibraryListItem key={item.id} item={item} quizProps={buildQuizProps} position={index} />} EmptyComponent={<EmptyQuizList type={'public'}
  // 폴더만 보는데 비었으면 퀴즈가 아니라 폴더를 만들게 한다.
  {...contentType === QuizsetCursorSearchReqContentType.FOLDER && {
    actionSlot: <CreateFolderEmptyAction />
  }} />} ErrorComponent={<ErrorBoundaryFallback />} LoadingComponent={null} data-sentry-element="InfiniteListView" data-sentry-component="QuizList" data-sentry-source-file="QuizList.tsx" />;
};

/** 자료실 `폴더` 빈 상태의 액션. 기본 `퀴즈 만들기` CTA 와 같은 모양으로 폴더 만들기 모달을 연다. */
const CreateFolderEmptyAction = () => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const {
    user
  } = useUser();
  const handleClick = () => {
    // 자료실은 비로그인도 본다. 서버 왕복 없이 바로 로그인으로 보낸다.
    if (!user) {
      void router.push({
        pathname: '/login',
        query: {
          callbackUrl: router.asPath
        }
      });
      return;
    }
    void showCreateFolderModal({
      place: 'library_empty'
    });
  };
  return <Button intent={'alternative'} variant={'outlined'} size={'lg'} rounded={'full'} onClick={handleClick} data-sentry-element="Button" data-sentry-component="CreateFolderEmptyAction" data-sentry-source-file="QuizList.tsx">
      <PlusIcon size={'md'} data-sentry-element="PlusIcon" data-sentry-source-file="QuizList.tsx" />
      {t('common:folderCreate.title')}
    </Button>;
};